import React from 'react';
import axios from 'utils/axios';
import { useSearchParams, useParams } from 'react-router-dom';
import { Buffer } from 'buffer';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Button,
    Fab,
    Grid,
    IconButton,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Typography,
    useMediaQuery,
    InputAdornment,
    TextField,
    CircularProgress,
    Box,
    Drawer,
    Backdrop
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// project imports
import { gridSpacing } from 'store/constant';
import { useDispatch, useSelector } from 'store';
import { ProgressList } from 'types/process';
import { getProcessList, reprocessLoadingState, searchProcess } from 'store/slices/process';
import ProcessFilters from './ProcessFilters';
import MainCard from 'ui-component/cards/MainCard';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { IconAlertTriangle, IconCircleCheck, IconFileDownload, IconFileSearch, IconRotate2 } from '@tabler/icons';
import FilterListIcon from '@mui/icons-material/FilterListTwoTone';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { MiscellaneousServices, ReadMore, RotateRight } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import ReactJson from 'react-json-view';

// ==============================|| PROCESS LIST ||============================== //

const ENV = process.env;

const ProcessCard = ({ process }: { process: string }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const matchDownXs = useMediaQuery(theme.breakpoints.down('sm'));

    const [searchParams, setSearchParams] = useSearchParams();
    const [rows, setRows] = React.useState<ProgressList[]>([]);
    const [countDown, setCountDown] = React.useState(100);
    const [autoReload, setAutoReload] = React.useState(false);
    const { processData, reprocessLoading, nextPageToken, lastPageToken, loading } = useSelector((state) => state.process);
    const [openFilterDrawer, setOpenFilterDrawer] = React.useState(false);
    const [openBottomDrawer, setOpenBottomDrawer] = React.useState(false);
    const [bottomDrawerData, setBottomDrawerData] = React.useState();
    const [downloadingLogs, setDownloadingLogs] = React.useState<string[]>([]);
    const [downloadingItems, setDownloadingItems] = React.useState<string[]>([]);
    const { slug: search } = useParams();

    const dateRangeInit = searchParams.get('dateRangeInit') || '';
    const dateRangeEnd = searchParams.get('dateRangeEnd') || '';
    const storeId = searchParams.get('storeId') || '';
    const status = searchParams.get('status') || '';
    const defaultDateRange = {
        dateRangeInit: new Date().setTime(new Date().getTime() - 10000 * 60),
        dateRangeEnd: new Date().getTime()
    };

    const blockSX = {
        p: 2,
        border: '1px solid ',
        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[200]
    };

    const fetchData = React.useCallback(() => {
        if ((dateRangeInit && dateRangeEnd) || search) {
            const data = {
                initDateTime: dateRangeInit,
                endDateTime: dateRangeEnd,
                limit: 10,
                process,
                storeId,
                status
                // ...(nextPageToken && nextPageToken !== lastPageToken && { nextPageToken })
            };

            const searchData = {
                search,
                limit: 50
                // ...(nextPageToken && nextPageToken !== lastPageToken && { nextPageToken })
            };

            (search && dispatch(searchProcess(searchData))) || dispatch(getProcessList(data));
        }
    }, [dateRangeInit, dateRangeEnd, search, process, storeId, status, dispatch]);

    React.useEffect(() => {
        if (!autoReload) {
            setAutoReload(false);
            setRows([]);
        }
    }, [searchParams, search, autoReload]);

    React.useEffect(() => {
        if (!search && !dateRangeInit && !dateRangeEnd) {
            setAutoReload(false);
            setSearchParams({
                ...Object.fromEntries(new URLSearchParams(searchParams)),
                ...{
                    dateRangeInit: new Date(defaultDateRange.dateRangeInit).toISOString(),
                    dateRangeEnd: new Date(defaultDateRange.dateRangeEnd).toISOString()
                }
            });
        }
    }, [searchParams, search, dateRangeInit, dateRangeEnd, defaultDateRange.dateRangeInit, defaultDateRange.dateRangeEnd, setSearchParams]);

    React.useEffect(() => {
        setRows((prev) =>
            [...processData, ...prev]
                .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
                .filter((item, index, self) => index === self.findIndex((t) => t.documentId === item.documentId))
        );

        // setRows(processData);
    }, [processData]);

    React.useEffect(() => {
        const interval = setInterval(() => {
            autoReload && setCountDown((prevProgress) => (prevProgress >= 100 ? 0 : prevProgress + 10));
            countDown === 90 &&
                setSearchParams({
                    ...Object.fromEntries(new URLSearchParams(searchParams)),
                    ...{
                        dateRangeInit,
                        dateRangeEnd: new Date().toISOString()
                    }
                });
        }, 3000);
        return () => clearInterval(interval);
    }, [autoReload, countDown, dateRangeInit, searchParams, setSearchParams]);

    React.useEffect(() => {
        fetchData();
    }, [searchParams, search, fetchData]);

    const handleReprocess = async (row: { documentId: string; process: string; storeId: string }) => {
        dispatch(reprocessLoadingState(row.documentId, false));
        await axios.post(
            `${ENV.REACT_APP_SERVICES_URL}/${(row.process || process || '').toLowerCase()}/reprocess/${row.storeId}/${row.documentId}`
        );

        setTimeout(() => {
            fetchData();
        }, 1500);

        setTimeout(() => {
            dispatch(reprocessLoadingState(row.documentId, true));
        }, 3000);
    };

    const handleDownload = async (url: string, filename: string) => {
        try {
            const response = await axios.get(url, { responseType: 'blob' });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = blobUrl;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Error downloading file:', error);
            throw error;
        }
    };

    const handleSetAutoReload = () => {
        setAutoReload(!autoReload);
        fetchData();
    };

    const handleFilters = (params: {
        process?: string;
        storeId?: string;
        status?: string;
        dateRangeInit?: string;
        dateRangeEnd?: string;
    }) => {
        setAutoReload(false);
        setSearchParams({ ...Object.fromEntries(new URLSearchParams(searchParams)), ...params });
    };

    const handleNextPage = () => {
        setAutoReload(false);
        fetchData();
    };

    const toggleFilterDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }

        setOpenFilterDrawer(open);
        setOpenBottomDrawer(false);
    };

    const toggleBottomDrawer = (open: boolean, data?: any) => (event: React.KeyboardEvent | React.MouseEvent) => {
        if (event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
            return;
        }

        setOpenFilterDrawer(false);
        setBottomDrawerData(data);
        setOpenBottomDrawer(open);
    };

    return (
        <MainCard
            title={
                <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
                    {search && (
                        <Grid item xs={12} sm={6}>
                            {search}
                        </Grid>
                    )}
                    {!search && (
                        <Grid item xs={12} sm={1}>
                            {process}
                        </Grid>
                    )}
                    {!search && (
                        <Grid item xs={12} sm={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <MobileDateTimePicker
                                    value={searchParams.get('dateRangeInit')}
                                    onChange={(date) =>
                                        handleFilters({
                                            dateRangeInit: date?.toISOString()
                                        })
                                    }
                                    onError={console.log}
                                    minDate={new Date('2018-01-01T00:00')}
                                    inputFormat="dd/MM/yyyy hh:mm a"
                                    mask="___/__/__ __:__ _M"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            margin="normal"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <DateRangeIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )}
                    {!search && (
                        <Grid item xs={12} sm={3}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <MobileDateTimePicker
                                    value={searchParams.get('dateRangeEnd')}
                                    onChange={(date) =>
                                        handleFilters({
                                            dateRangeEnd: date?.toISOString()
                                        })
                                    }
                                    onError={console.log}
                                    minDate={new Date('2018-01-01T00:00')}
                                    inputFormat="dd/MM/yyyy hh:mm a"
                                    mask="___/__/__ __:__ _M"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            margin="normal"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <DateRangeIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    )}
                                />
                            </LocalizationProvider>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={5} sx={{ textAlign: 'right' }}>
                        {/* <Tooltip title="loading">
                            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                            </Box>
                        </Tooltip> */}
                        <Tooltip title="Auto Reload (1m)">
                            <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                <CircularProgress value={countDown} variant="determinate" />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: -8,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Fab
                                        color="primary"
                                        size="small"
                                        sx={{ boxShadow: 'none', ml: 1, width: 32, height: 32, minHeight: 32 }}
                                        onClick={() => handleSetAutoReload()}
                                    >
                                        {!autoReload && <PlayArrowIcon />}
                                        {autoReload && <PauseIcon />}
                                    </Fab>
                                </Box>
                            </Box>
                        </Tooltip>

                        {!search && (
                            <Tooltip title="Filter">
                                <IconButton size="large" onClick={toggleFilterDrawer(true)}>
                                    <FilterListIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
            }
        >
            <Backdrop open={loading}>
                <CircularProgress />
            </Backdrop>

            <TableContainer>
                <Table
                    sx={{
                        '& td': {
                            whiteSpace: 'nowrap'
                        },
                        '& td:first-of-type': {
                            pl: 0
                        },
                        '& td:last-of-type': {
                            pr: 0,
                            minWidth: '100%'
                        },
                        '& tbody tr:last-of-type  td': {
                            borderBottom: 'none'
                        },
                        [theme.breakpoints.down('lg')]: {
                            '& tr:not(:last-of-type)': {
                                borderBottom: '1px solid',
                                borderBottomColor: theme.palette.mode === 'dark' ? 'rgb(132, 146, 196, .2)' : 'rgba(224, 224, 224, 1)'
                            },
                            '& td': {
                                display: 'inline-block',
                                borderBottom: 'none',
                                pl: 0
                            },
                            '& td:first-of-type': {
                                display: 'block'
                            }
                        }
                    }}
                >
                    <TableBody>
                        {rows.map((row, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Grid container spacing={3} alignItems="center">
                                        <Grid item>
                                            {row.progressStatus === 'FINALIZED' && row.success >= row.total && (
                                                <CheckCircleIcon sx={{ color: 'success.dark', width: 60, height: 60 }} />
                                            )}
                                            {row.progressStatus === 'FINALIZED' && row.success < row.total && (
                                                <WarningIcon sx={{ color: 'warning.dark', width: 60, height: 60 }} />
                                            )}
                                            {row.progressStatus === 'ERROR' && (
                                                <ErrorIcon sx={{ color: 'error.dark', width: 60, height: 60 }} />
                                            )}
                                            {row.progressStatus === 'PROCESSING' && (
                                                <MiscellaneousServices sx={{ width: 60, height: 60 }} />
                                            )}
                                            {row.progressStatus === 'INITIALIZING' && <RotateRight sx={{ width: 60, height: 60 }} />}
                                        </Grid>
                                        <Grid item sm zeroMinWidth>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <Typography align="left" variant="subtitle1">
                                                        {row.documentId}
                                                    </Typography>
                                                    <Typography
                                                        align="left"
                                                        variant="subtitle1"
                                                        sx={{
                                                            whiteSpace: 'break-spaces',
                                                            fontWeight: 'bold',
                                                            color: row.progressStatus === 'ERROR' ? 'error.dark' : 'primary.dark'
                                                        }}
                                                    >
                                                        {row.progressStatus}{' '}
                                                        {row.progressStatus !== 'ERROR' && (
                                                            <DoneAllIcon sx={{ color: 'primary.dark', width: 18, height: 18 }} />
                                                        )}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                            <Grid container alignItems="center" spacing={0}>
                                                <Grid item xs={12} sm={6} sx={blockSX}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        spacing={1}
                                                        justifyContent={matchDownXs ? 'space-between' : 'center'}
                                                    >
                                                        <Grid item>
                                                            <IconCircleCheck stroke={1.5} />
                                                        </Grid>
                                                        <Grid item sm zeroMinWidth>
                                                            <Typography variant="h5" align="center">
                                                                {row?.success?.toLocaleString()}
                                                            </Typography>
                                                            <Typography variant="subtitle2" align="center">
                                                                SUCCESS
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                                <Grid item xs={12} sm={6} sx={blockSX}>
                                                    <Grid
                                                        container
                                                        alignItems="center"
                                                        spacing={1}
                                                        justifyContent={matchDownXs ? 'space-between' : 'center'}
                                                    >
                                                        <Grid item>
                                                            <IconAlertTriangle stroke={1.5} />
                                                        </Grid>
                                                        <Grid item sm zeroMinWidth>
                                                            <Typography variant="h5" align="center">
                                                                {row?.errors?.toLocaleString()}
                                                            </Typography>
                                                            <Typography variant="subtitle2" align="center">
                                                                ERROR
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TableCell>
                                <TableCell>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="caption">Start Time</Typography>
                                            <Typography variant="h6">{new Date(row.startTime).toLocaleString('es-MX')}</Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption">Elapsed Time</Typography>
                                            <Typography variant="h6">{row.elapsedTime}</Typography>
                                        </Grid>
                                    </Grid>
                                </TableCell>
                                <TableCell>
                                    <Grid container spacing={1}>
                                        <Grid item xs={8}>
                                            <Typography variant="caption">StoreId</Typography>
                                            <Typography variant="h6">{row.storeId}</Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant="caption">Requests x Second</Typography>
                                            <Typography variant="h6">{row.rps}</Typography>
                                        </Grid>
                                    </Grid>
                                </TableCell>
                                <TableCell>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <Grid container alignItems="center" spacing={gridSpacing}>
                                                <Grid item xs={12} lg={12} sm={12}>
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={12}>
                                                            <Typography variant="subtitle2" align="left">
                                                                Progress
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <Typography variant="h3" align="left">
                                                                {row.progress}%
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <LinearProgress variant="determinate" value={row.progress} color="secondary" />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={1}>
                                            <Grid item xs={3}>
                                                <LoadingButton
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    startIcon={<IconFileSearch />}
                                                    loading={downloadingLogs.includes(row.documentId)}
                                                    onClick={async () => {
                                                        setDownloadingLogs((prev) => [...prev, row.documentId]);
                                                        const logGroupNameBase64 = Buffer.from(row.logGroupName?.[0] || '').toString(
                                                            'base64'
                                                        );
                                                        const logStreamNameBase64 = Buffer.from(row.logStreamName?.[0] || '').toString(
                                                            'base64'
                                                        );
                                                        const url = `${ENV.REACT_APP_SERVICES_URL}/${(
                                                            row.process ||
                                                            process ||
                                                            ''
                                                        ).toLowerCase()}/logs/${logGroupNameBase64}/${logStreamNameBase64}`;
                                                        try {
                                                            await handleDownload(url, `logs-${row.documentId}.txt`);
                                                        } catch (error) {
                                                            // error handled in handleDownload
                                                        } finally {
                                                            setDownloadingLogs((prev) => prev.filter((id) => id !== row.documentId));
                                                        }
                                                    }}
                                                >
                                                    Logs
                                                </LoadingButton>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <LoadingButton
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    startIcon={<IconFileDownload />}
                                                    loading={downloadingItems.includes(row.documentId)}
                                                    onClick={async () => {
                                                        setDownloadingItems((prev) => [...prev, row.documentId]);
                                                        const url = `${ENV.REACT_APP_SERVICES_URL}/${(
                                                            row.process ||
                                                            process ||
                                                            ''
                                                        ).toLowerCase()}/download/${row.storeId}/${row.documentId}`;
                                                        try {
                                                            await handleDownload(url, `${row.documentId}.json`);
                                                        } catch (error) {
                                                            // error handled in handleDownload
                                                        } finally {
                                                            setDownloadingItems((prev) => prev.filter((id) => id !== row.documentId));
                                                        }
                                                    }}
                                                >
                                                    Items
                                                </LoadingButton>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Button
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    color="error"
                                                    startIcon={<IconAlertTriangle />}
                                                    disabled={!row.errorData}
                                                    onClick={toggleBottomDrawer(true, {
                                                        documentId: row.documentId,
                                                        storeId: row.storeId,
                                                        errorData: row.errorData
                                                    })}
                                                >
                                                    Errors
                                                </Button>
                                            </Grid>
                                            <Grid item xs={3}>
                                                <LoadingButton
                                                    variant="outlined"
                                                    fullWidth
                                                    color="secondary"
                                                    size="small"
                                                    startIcon={<IconRotate2 />}
                                                    loading={reprocessLoading.includes(row.documentId)}
                                                    onClick={async () => handleReprocess(row)}
                                                >
                                                    Reprocess
                                                </LoadingButton>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Grid container direction="column" alignItems="center" justifyContent="center">
                <Button
                    variant="outlined"
                    size="large"
                    color="primary"
                    startIcon={<ReadMore />}
                    onClick={() => handleNextPage()}
                    disabled={lastPageToken === nextPageToken}
                >
                    Load More Results
                </Button>
            </Grid>
            <Drawer anchor="right" open={openFilterDrawer} onClose={toggleFilterDrawer(false)}>
                <ProcessFilters />
            </Drawer>
            <Drawer anchor="right" open={openBottomDrawer} onClose={toggleBottomDrawer(false)} sx={{ height: '50vh' }}>
                <Box overflow="auto" margin={2} maxWidth={650}>
                    <Typography variant="body1" align="left">
                        <ReactJson src={bottomDrawerData || {}} theme="twilight" enableClipboard displayDataTypes={false} />
                    </Typography>
                </Box>
            </Drawer>
        </MainCard>
    );
};

export default ProcessCard;
